import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { CONTACT_INFO, NAV_LINKS, SERVICES } from "@/lib/data";

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#040406] border-t border-white/5">
      <div className="absolute inset-x-0 top-0 glow-line-mag" />
      <div className="absolute inset-x-0 top-[1px] glow-line-cy opacity-40" />

      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          <div className="md:col-span-4">
            <Logo size={62} />
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm font-body">
              Premium event photography and cinematic visual storytelling. We capture life’s
              finest moments with the elegance, precision and craft they deserve.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[Instagram, Facebook, MessageCircle].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 inline-flex items-center justify-center rounded-full glass chrome-border text-white/70 hover:text-white transition-colors"
                  aria-label="social"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-italiana text-white text-lg mb-5 tracking-[0.18em]">Navigate</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/55 hover:text-white text-sm transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/auth" className="text-white/55 hover:text-white text-sm transition-colors">
                  Login / Register
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-italiana text-white text-lg mb-5 tracking-[0.18em]">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 8).map((s) => (
                <li key={s.title} className="text-white/55 text-sm">{s.title}</li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-italiana text-white text-lg mb-5 tracking-[0.18em]">Studio</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-white/65">
                <MapPin className="w-4 h-4 mt-0.5 text-[#ff2e93]" />
                {CONTACT_INFO.location}
              </li>
              <li className="flex items-center gap-3 text-white/65">
                <Mail className="w-4 h-4 text-[#18d3ff]" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white">{CONTACT_INFO.email}</a>
              </li>
              <li className="flex items-center gap-3 text-white/65">
                <Phone className="w-4 h-4 text-[#ff2e93]" />
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="hover:text-white">{CONTACT_INFO.phone}</a>
              </li>
              <li className="flex items-center gap-3 text-white/65">
                <MessageCircle className="w-4 h-4 text-[#18d3ff]" />
                WhatsApp on request
              </li>
            </ul>
          </div>
        </div>

        <div className="hairline mt-16" />

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40 tracking-[0.22em] uppercase">
            © {new Date().getFullYear()} WelkomBiz Photography — Capturing Moments in Timeless Elegance
          </p>
          <p className="text-xs text-white/30 tracking-[0.18em] uppercase">Crafted for the Goldfields · ZA</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
